import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { trackerAPI } from '../api/tracker.api';
import type { TrackerChannel, TrackerListFilters, TrackerListStats } from '../types/tracker.types';
import { useAuth } from '@/core/auth/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import { getStatusGroupId } from '../constants/statusGroups.constants';
import { PREDEFINED_RULES } from '../utils/statusGroupRules';
import { useDebounce } from './useDebounce';

// Valid filter values for type safety
const VALID_FILTERS = ['all', 'subscribed', 'my_affaires', 'a_faire', 'specific_user'] as const;
const VALID_SORT_BY = ['last_activity_date', 'created_at', 'status', 'name', 'affaire_id', 'unread_count', 'active_count', 'urgency', 'lateness'] as const;
const VALID_SORT_ORDER = ['asc', 'desc'] as const;

/**
 * Check if a search query should trigger a database search
 * - Numeric-only searches (affaire numbers) trigger immediately
 * - Text searches require minimum 4 characters
 */
const shouldTriggerDatabaseSearch = (query: string): boolean => {
  const trimmed = query.trim();
  if (!trimmed) return false;
  // Numeric-only (affaire number): no minimum
  if (/^\d+$/.test(trimmed)) return true;
  // Text search: minimum 4 characters
  return trimmed.length >= 4;
};

/**
 * Calculate how many days "late" an affaire is based on its status group rules
 * Returns a number representing lateness (higher = more urgent)
 * Returns -1 for affaires with no applicable rule or no limit
 */
const calculateLateness = (channel: TrackerChannel): number => {
  const statusGroupId = getStatusGroupId(channel.status);
  if (statusGroupId === undefined) return -1;

  // Find the applicable rule for this status group
  const applicableRule = PREDEFINED_RULES.find(
    rule => rule.enabled && rule.statusGroupIds.includes(statusGroupId)
  );

  if (!applicableRule || applicableRule.conditions.length === 0) {
    // No rule or no limit for this status group
    return -1;
  }

  // Find the days_since_activity condition
  const daysCondition = applicableRule.conditions.find(
    cond => cond.type === 'days_since_activity'
  );

  if (!daysCondition) return -1;

  const threshold = daysCondition.value as number;
  const daysSinceActivity = channel.days_since_activity ?? 0;

  // Calculate how many days over the threshold
  const lateness = daysSinceActivity - threshold;

  // Return lateness (can be negative if within threshold)
  return lateness;
};

// Session storage key for filter persistence
const TRACKER_FILTERS_STORAGE_KEY = 'tracker-list-filters';

/**
 * Hook to fetch and manage tracker channels list
 * Similar to useMesDossiers but for tracker channels
 *
 * Filter state is persisted in:
 * 1. URL search params (for sharing/bookmarking)
 * 2. sessionStorage (for navigation back from detail pages)
 */
export const useTrackerList = () => {
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const channelsDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const todosDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Parse initial filter state from URL params or sessionStorage
  const getInitialFilters = useCallback((): TrackerListFilters => {
    // First, try to get from URL params
    const filterParam = searchParams.get('filter');
    const hasUrlParams = filterParam || searchParams.get('q') || searchParams.get('sortBy');

    // If URL has params, use them
    if (hasUrlParams) {
      const sortByParam = searchParams.get('sortBy');
      const sortOrderParam = searchParams.get('sortOrder');
      const responsableParam = searchParams.get('responsable');
      const showClosedParam = searchParams.get('showClosed');
      const statusGroupsParam = searchParams.get('statusGroups');
      const showUnreadOnlyParam = searchParams.get('unreadOnly');

      return {
        searchQuery: searchParams.get('q') || '',
        sortBy: (sortByParam && VALID_SORT_BY.includes(sortByParam as typeof VALID_SORT_BY[number]))
          ? sortByParam as TrackerListFilters['sortBy']
          : 'last_activity_date',
        sortOrder: (sortOrderParam && VALID_SORT_ORDER.includes(sortOrderParam as typeof VALID_SORT_ORDER[number]))
          ? sortOrderParam as 'asc' | 'desc'
          : 'desc',
        activeFilter: (filterParam && VALID_FILTERS.includes(filterParam as typeof VALID_FILTERS[number]))
          ? filterParam as TrackerListFilters['activeFilter']
          : 'my_affaires',
        selectedResponsableName: responsableParam || null,
        visibleStatusGroups: statusGroupsParam
          ? statusGroupsParam.split(',').map(Number).filter(n => !isNaN(n))
          : [],
        statusSortOrder: 'asc',
        showClosed: showClosedParam === 'true',
        myAffairesSubFilter: 'all',
        showUnreadOnly: showUnreadOnlyParam === 'true'
      };
    }

    // Otherwise, try to restore from sessionStorage (for back navigation from detail page)
    try {
      const stored = sessionStorage.getItem(TRACKER_FILTERS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<TrackerListFilters>;
        return {
          searchQuery: parsed.searchQuery || '',
          sortBy: parsed.sortBy || 'last_activity_date',
          sortOrder: parsed.sortOrder || 'desc',
          activeFilter: (parsed.activeFilter && VALID_FILTERS.includes(parsed.activeFilter as typeof VALID_FILTERS[number]))
            ? parsed.activeFilter
            : 'my_affaires',
          selectedResponsableName: parsed.selectedResponsableName || null,
          visibleStatusGroups: parsed.visibleStatusGroups || [],
          statusSortOrder: parsed.statusSortOrder || 'asc',
          showClosed: parsed.showClosed || false,
          myAffairesSubFilter: parsed.myAffairesSubFilter || 'all',
          showUnreadOnly: parsed.showUnreadOnly || false
        };
      }
    } catch (e) {
      console.error('[TrackerList] Error parsing stored filters:', e);
    }

    // Default values
    return {
      searchQuery: '',
      sortBy: 'last_activity_date',
      sortOrder: 'desc',
      activeFilter: 'a_faire',
      selectedResponsableName: null,
      visibleStatusGroups: [],
      statusSortOrder: 'asc',
      showClosed: false,
      myAffairesSubFilter: 'all',
      showUnreadOnly: false
    };
  }, [searchParams]);

  const [filters, setFilters] = useState<TrackerListFilters>(getInitialFilters);

  // Database search state
  const [searchResults, setSearchResults] = useState<TrackerChannel[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchQuery = useDebounce(filters.searchQuery, 300);

  // Sync filter changes to URL params AND sessionStorage
  useEffect(() => {
    // Save to sessionStorage for back navigation
    try {
      sessionStorage.setItem(TRACKER_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
      console.error('[TrackerList] Error saving filters to sessionStorage:', e);
    }

    // Update URL params (only for non-default values to keep URL clean)
    const params = new URLSearchParams();

    if (filters.activeFilter && filters.activeFilter !== 'my_affaires') {
      params.set('filter', filters.activeFilter);
    }
    if (filters.searchQuery) {
      params.set('q', filters.searchQuery);
    }
    if (filters.sortBy && filters.sortBy !== 'last_activity_date') {
      params.set('sortBy', filters.sortBy);
    }
    if (filters.sortOrder && filters.sortOrder !== 'desc') {
      params.set('sortOrder', filters.sortOrder);
    }
    if (filters.selectedResponsableName) {
      params.set('responsable', filters.selectedResponsableName);
    }
    if (filters.showClosed) {
      params.set('showClosed', 'true');
    }
    if (filters.visibleStatusGroups.length > 0) {
      params.set('statusGroups', filters.visibleStatusGroups.join(','));
    }
    if (filters.showUnreadOnly) {
      params.set('unreadOnly', 'true');
    }

    // Update URL without adding to history stack (replace)
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Database search effect - calls backend API when search threshold is met
  useEffect(() => {
    if (shouldTriggerDatabaseSearch(debouncedSearchQuery)) {
      setIsSearching(true);
      trackerAPI.searchChannels(debouncedSearchQuery)
        .then(response => {
          setSearchResults(response.data || []);
        })
        .catch(err => {
          console.error('[TrackerList] Database search error:', err);
          setSearchResults([]);
        })
        .finally(() => setIsSearching(false));
    } else {
      // Clear search results when query is empty or below threshold
      setSearchResults(null);
    }
  }, [debouncedSearchQuery]);

  // Fetch all channels (no subscription filter - showing all by default)
  const {
    data: channelsData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['tracker-channels-all'],
    queryFn: () => trackerAPI.getAllChannels(false, 2000, 0), // limit: 2000, offset: 0
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });

  const channels = channelsData?.data || [];
  const pagination = channelsData?.pagination;

  // State to track todo affaire IDs for 'a_faire' filter
  const [todoAffaireIds, setTodoAffaireIds] = useState<Set<number>>(new Set());

  // Function to fetch todos (used by both filter change and WebSocket events)
  const fetchTodos = useCallback(async () => {
    try {
      // Pass showClosed to get either open or closed todos
      const response = await trackerAPI.getMyTodos(filters.showClosed) as {
        success: boolean;
        data: Array<{ affaire_id: number }>;
      };
      if (response.success && response.data) {
        const ids = new Set(response.data.map(todo => todo.affaire_id));
        setTodoAffaireIds(ids);
      }
    } catch (error) {
      console.error('[TrackerList] Error fetching my todos:', error);
    }
  }, [filters.showClosed]);

  // Fetch todos on mount (needed for notification badge count) and when filter changes
  // Always fetch to ensure notification badges show correct counts
  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  // Debounced refetch for channels (prevents rapid-fire API calls from WebSocket events)
  const debouncedRefetchChannels = useCallback(() => {
    if (channelsDebounceRef.current) {
      clearTimeout(channelsDebounceRef.current);
    }
    channelsDebounceRef.current = setTimeout(() => {
      console.log('[TrackerList] Debounced channels refetch triggered');
      queryClient.invalidateQueries({ queryKey: ['tracker-channels-all'] });
    }, 500); // 500ms debounce for heavy channel request
  }, [queryClient]);

  // Debounced refetch for todos (prevents rapid-fire API calls from WebSocket events)
  const debouncedFetchTodos = useCallback(() => {
    if (todosDebounceRef.current) {
      clearTimeout(todosDebounceRef.current);
    }
    todosDebounceRef.current = setTimeout(() => {
      console.log('[TrackerList] Debounced todos refetch triggered');
      fetchTodos();
    }, 300);
  }, [fetchTodos]);

  // Listen for real-time tracker activity updates
  // CONSOLIDATED HANDLERS for better maintainability
  useEffect(() => {
    if (!socket || !isConnected) return;

    /**
     * Handler for events that affect the "À faire" list (todos).
     * These events may add/remove items from user's todo list:
     * - tracker:affaire:tagged - User tagged in affaire
     * - tracker:communication:assigned - Communication assigned to user
     * - tracker:communication:completed - Communication marked done
     * - tracker:status:changed - Status change (affects status-based todos)
     * - tracker:task:created/updated - Task changes may affect todos
     */
    const handleTodoAffectingEvent = (event: any) => {
      console.log('[Tracker] Todo-affecting event:', event?.type || 'unknown');
      if (filters.activeFilter === 'a_faire') {
        debouncedFetchTodos();
      }
      debouncedRefetchChannels();
    };

    /**
     * Handler for events that only affect channel list (activity, communications).
     * These update the channel list but don't affect todo membership:
     * - tracker:activity:updated - General activity update
     * - tracker:com:created/updated - New or updated communications
     * - tracker:task:comment:added - Comment added to task
     */
    const handleChannelOnlyEvent = (event: any) => {
      console.log('[Tracker] Channel-only event:', event?.type || 'unknown');
      debouncedRefetchChannels();
    };

    /**
     * Handler for responsable changes (specific to "mes affaires" filter)
     */
    const handleResponsableChanged = (event: any) => {
      console.log('[Tracker] Responsable changed:', event);
      if (filters.activeFilter === 'my_affaires') {
        debouncedRefetchChannels();
      }
    };

    // Events that affect "À faire" list (todos + channels)
    const todoEvents = [
      'tracker:affaire:tagged',
      'tracker:communication:assigned',
      'tracker:communication:completed',
      'tracker:status:changed',
      'tracker:task:created',
      'tracker:task:updated',
    ];

    // Events that only affect channel list
    const channelOnlyEvents = [
      'tracker:activity:updated',
      'tracker:com:created',
      'tracker:com:updated',
      'tracker:task:comment:added',
    ];

    // Subscribe to all events
    todoEvents.forEach(event => socket.on(event, handleTodoAffectingEvent));
    channelOnlyEvents.forEach(event => socket.on(event, handleChannelOnlyEvent));
    socket.on('tracker:responsable:changed', handleResponsableChanged);

    console.log('[Tracker] Listening for real-time updates');

    // Cleanup
    return () => {
      todoEvents.forEach(event => socket.off(event, handleTodoAffectingEvent));
      channelOnlyEvents.forEach(event => socket.off(event, handleChannelOnlyEvent));
      socket.off('tracker:responsable:changed', handleResponsableChanged);

      if (channelsDebounceRef.current) clearTimeout(channelsDebounceRef.current);
      if (todosDebounceRef.current) clearTimeout(todosDebounceRef.current);
    };
  }, [socket, isConnected, debouncedRefetchChannels, debouncedFetchTodos, filters.activeFilter]);

  // Extract unique responsable names for the filter dropdown
  const uniqueResponsables = useMemo(() => {
    const responsables = channels
      .map(c => c.responsable_name)
      .filter((name): name is string => !!name);
    return Array.from(new Set(responsables)).sort();
  }, [channels]);

  // Calculate stats from all channels
  const stats = useMemo((): TrackerListStats => {
    return {
      totalChannels: channels.length,
      subscribedChannels: channels.filter(c => c.is_subscribed).length,
      totalUnread: channels.reduce((sum, c) => sum + (c.total_unread_count ?? c.unread_task_count), 0),
      totalActive: channels.reduce((sum, c) => sum + c.active_task_count, 0),
    };
  }, [channels]);

  // Filter and sort channels
  const filteredChannels = useMemo(() => {
    // Debug logging for my_affaires filter
    if (filters.activeFilter === 'my_affaires') {
      console.log('[TrackerList] Filtering for my_affaires:', {
        userFmName: user?.fmName,
        totalChannels: channels.length,
        channelsWithResponsable: channels.filter(c => !!c.responsable_name).length,
        sampleResponsableNames: channels.slice(0, 5).map(c => c.responsable_name)
      });
    }

    const filtered = channels.filter(channel => {
      // Apply search filter
      if (filters.searchQuery.trim()) {
        const query = filters.searchQuery.toLowerCase();
        const matchesSearch =
          channel.name?.toLowerCase().includes(query) ||
          channel.affaire_id?.toString().includes(query) ||
          channel.adresse_rue_bien?.toLowerCase().includes(query) ||
          channel.commune_bien?.toLowerCase().includes(query) ||
          channel.description?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Note: We don't filter by statut_affaire (ARCHIVES, etc.)
      // Affaires are only filtered by is_active (active/désactivé) which is handled by the backend

      // Apply status group filter
      if (filters.visibleStatusGroups.length > 0) {
        const statusGroupId = getStatusGroupId(channel.status);
        // getStatusGroupId returns 999 for unmapped statuses, undefined only if status is null
        // Exclude channels without status or not in visible groups
        if (statusGroupId === undefined || !filters.visibleStatusGroups.includes(statusGroupId)) {
          return false;
        }
      }

      // Apply active filter
      switch (filters.activeFilter) {
        case 'subscribed':
          return channel.is_subscribed;
        case 'my_affaires': {
          // Match current user's fm_name with responsable_name
          // Use trim() and case-insensitive comparison for robustness
          if (!channel.responsable_name || !user?.fmName) {
            return false;
          }
          const channelResponsable = channel.responsable_name.trim().toUpperCase();
          const userFmName = user.fmName.trim().toUpperCase();
          return channelResponsable === userFmName;
        }
        case 'a_faire':
          // Show only channels where user is tagged with active todo
          return todoAffaireIds.has(channel.affaire_id);
        case 'specific_user':
          // Match selected responsable
          return filters.selectedResponsableName
            ? channel.responsable_name === filters.selectedResponsableName
            : false;
        case 'all':
        default:
          return true;
      }
    });

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (filters.sortBy) {
        case 'lateness': {
          // Sort by how many days "late" based on status group rules
          const latenessA = calculateLateness(a);
          const latenessB = calculateLateness(b);

          // If one has no rule (-1) and the other does, prioritize the one with a rule
          if (latenessA === -1 && latenessB !== -1) return 1;
          if (latenessB === -1 && latenessA !== -1) return -1;
          if (latenessA === -1 && latenessB === -1) {
            // Both have no rules, sort by days_since_activity as fallback
            comparison = (b.days_since_activity ?? 0) - (a.days_since_activity ?? 0);
          } else {
            // Sort by lateness (higher lateness = more urgent)
            comparison = latenessB - latenessA;
          }
          break;
        }

        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;

        case 'affaire_id':
          comparison = a.affaire_id - b.affaire_id;
          break;

        case 'created_at': {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          comparison = dateB.getTime() - dateA.getTime();
          break;
        }

        case 'last_activity_date': {
          // Sort by most recent activity (prioritize last_activity_date, fallback to updated_at)
          const activityA = new Date(a.last_activity_date || a.updated_at || 0);
          const activityB = new Date(b.last_activity_date || b.updated_at || 0);
          comparison = activityB.getTime() - activityA.getTime();
          break;
        }

        case 'unread_count':
          comparison = (b.total_unread_count ?? b.unread_task_count) - (a.total_unread_count ?? a.unread_task_count);
          break;

        case 'active_count':
          comparison = b.active_task_count - a.active_task_count;
          break;

        case 'status': {
          // Sort by status group ID
          // getStatusGroupId now returns 999 for unmapped, so we use 9999 for truly unknown
          const statusGroupIdA = getStatusGroupId(a.status) ?? 9999;
          const statusGroupIdB = getStatusGroupId(b.status) ?? 9999;
          comparison = statusGroupIdA - statusGroupIdB;
          // Use statusSortOrder instead of general sortOrder for status-based sorting
          return filters.statusSortOrder === 'asc' ? comparison : -comparison;
        }

        case 'urgency': {
          // Sort by urgency level (urgent = 1, high = 2, medium = 3, low = 4, none = 5, no todo = 6)
          const getPriorityWeight = (priority?: string) => {
            switch (priority) {
              case 'urgent': return 1;
              case 'high': return 2;
              case 'medium': return 3;
              case 'low': return 4;
              case 'none': return 5;
              default: return 6; // No todo
            }
          };

          const urgencyA = getPriorityWeight(a.todo?.priority);
          const urgencyB = getPriorityWeight(b.todo?.priority);
          comparison = urgencyA - urgencyB;
          break;
        }

        default:
          return 0;
      }

      // Apply sort order (except for status and lateness which handle their own)
      if (filters.sortBy === 'lateness') {
        return comparison; // Already handled in the lateness case
      }
      return filters.sortOrder === 'asc' ? -comparison : comparison;
    });

    return sorted;
  }, [channels, filters, user, todoAffaireIds]);

  // Determine if we're in database search mode
  const isSearchMode = searchResults !== null;

  return {
    // Data - use search results when in search mode, otherwise use locally filtered channels
    channels: isSearchMode ? searchResults : filteredChannels,
    allChannels: channels,
    stats,
    uniqueResponsables,
    pagination,
    todoAffaireIds, // For "À faire" notification count calculation

    // Loading states
    isLoading,
    isSearching, // Loading state for database search
    error,

    // Search mode flag - when true, other filters are bypassed
    isSearchMode,

    // Filters
    filters,
    setFilters,
    updateFilter: (key: keyof TrackerListFilters, value: any) => {
      setFilters(prev => ({ ...prev, [key]: value }));
    },

    // Actions
    refetch
  };
};
